// @ts-check
// Regras puras do painel "Quem é" (contrato 10 de atendimento, seções 5.6 e 18): o resumo do que o pedinte pode
// receber de cada aluno, a restrição judicial em destaque e quem pode ser escolhido como falante.

/** Decisões de divulgação (seção 5.6) que a tela mostra. */
export const DISCLOSURE_DECISIONS = Object.freeze(/** @type {const} */ (['ALLOWED', 'DENIED', 'BLOCKED', 'NEEDS_STRONG_VERIFICATION']));
/** Motivos de recusa da divulgação (seção 5.6). */
export const DISCLOSURE_REASONS = Object.freeze(/** @type {const} */ (['LEGAL_RESTRICTION', 'NOT_LINKED', 'ADULT_STUDENT', 'NOT_FINANCIAL_GUARDIAN', 'NOT_GUARDIAN', 'PICK_UP_ONLY']));

/**
 * @typedef {{ decision: string, reason?: string | null } | string | null | undefined} DisclosureValue
 * @typedef {{ PEDAGOGICAL?: DisclosureValue, FINANCIAL?: DisclosureValue, ADMINISTRATIVE?: DisclosureValue } | null | undefined} StudentDisclosure
 */

/** @param {DisclosureValue} value */
const decisionOf = (value) => (typeof value === 'string' ? value : value?.decision ?? 'DENIED');

/**
 * Resumo do que pode ser informado sobre o aluno ao pedinte (seção 5.6), com o tom do selo; a chave fica em
 * serviceDesk.whoIs.disclosure.*.
 * @param {StudentDisclosure} disclosure
 * @returns {{ key: string, tone: 'ok' | 'partial' | 'blocked' | 'warning' | 'none' }}
 */
export function summarizeDisclosure(disclosure) {
  const pedagogical = decisionOf(disclosure?.PEDAGOGICAL);
  const financial = decisionOf(disclosure?.FINANCIAL);
  const administrative = decisionOf(disclosure?.ADMINISTRATIVE);
  const all = [pedagogical, financial, administrative];
  if (all.includes('BLOCKED')) return { key: 'blocked', tone: 'blocked' };
  if (all.includes('NEEDS_STRONG_VERIFICATION')) return { key: 'needsCode', tone: 'warning' };
  const allowsSchool = pedagogical === 'ALLOWED' && administrative === 'ALLOWED';
  if (allowsSchool && financial === 'ALLOWED') return { key: 'everything', tone: 'ok' };
  if (allowsSchool) return { key: 'pedagogicalAndSecretary', tone: 'partial' };
  if (financial === 'ALLOWED') return { key: 'financialOnly', tone: 'partial' };
  if (administrative === 'ALLOWED') return { key: 'secretaryOnly', tone: 'partial' };
  return { key: 'generalOnly', tone: 'none' };
}

/**
 * Restrições judiciais vigentes nos vínculos do falante, para o aviso "Restrição judicial: não informar".
 * @param {Array<{ householdName?: string | null, legalRestriction?: { isActive: boolean, kind: string } | null }> | null | undefined} links
 */
export function activeLegalRestrictions(links) {
  return (links ?? []).filter((link) => link.legalRestriction?.isActive).map((link) => ({ householdName: link.householdName ?? '', kind: link.legalRestriction?.kind ?? 'OTHER' }));
}

/**
 * Quem pode ser escolhido como falante: os candidatos do telefone e os membros da família, sem repetir e sem o atual.
 * @param {{ speaker?: { personId: string } | null, candidates?: Array<{ personId: string, firstName: string }> } | null | undefined} whoIs
 */
export function speakerChoices(whoIs) {
  /** @type {Map<string, { personId: string, firstName: string }>} */
  const byId = new Map();
  for (const candidate of whoIs?.candidates ?? []) if (candidate.personId !== whoIs?.speaker?.personId) byId.set(candidate.personId, candidate);
  return [...byId.values()];
}

/** O falante precisa de um código para assunto sensível? @param {string | null | undefined} level */
export function needsStrongVerification(level) {
  return level !== 'STRONG_CODE' && level !== 'TEAM';
}
